import { getCountries } from 'libphonenumber-js';
import { LocationCountryCode } from 'src/api/generated';

/**
 * Builds localized country choices from the app's existing country metadata.
 * @param language Locale used for labels and sorting.
 * @param currentCountryCode Saved/provider country to retain if absent from metadata.
 * @returns Country options with ISO codes and English search aliases.
 */
export const buildLocationCountryOptions = (
  language: string,
  currentCountryCode?: string | null
): { content: string; value: string; keywords: string }[] => {
  const names = new Intl.DisplayNames([language], { type: 'region' });
  const englishNames = new Intl.DisplayNames(['en'], { type: 'region' });
  const countryCodes = new Set<string>(getCountries());
  const currentCode = currentCountryCode?.trim().toUpperCase();
  if (currentCode && /^[A-Z]{2}$/.test(currentCode)) {
    countryCodes.add(currentCode);
  }
  return Array.from(countryCodes, (value) => ({
    content: names.of(value) ?? value,
    value,
    keywords: `${value} ${englishNames.of(value) ?? ''}`,
  })).sort((left, right) =>
    left.content.localeCompare(right.content, language)
  );
};

/**
 * Resolves an API-supported catalog country without falling back to the tenant.
 * @param countryCode Property country whose administrative catalog is needed.
 * @returns Supported catalog code, or undefined when no endpoint supports it.
 */
export const getLocationCatalogCountryCode = (
  countryCode?: string | null
): LocationCountryCode | undefined =>
  Object.values(LocationCountryCode).find(
    (code) => code === countryCode?.trim().toUpperCase()
  );
